/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

/// these options can be combined together to affect the render graph traversal
import {AbstractRenderGraph} from "App/render/abstract_render_graph";
import Container from "App/utils/container";
import WebGLContext from "App/web_gl_context";
import {Framebuffer} from "App/utils/frame_buffer";
import Canvas2DContext from "App/canvas_2d_context";
import ControlsLayer from "App/render/webgl/layers/controls_layer";
import HUDLayer from "App/render/webgl/layers/hud_layer";
import ShadowMapLayer from "App/render/webgl/layers/shadow_map_layer";
import SSAOLayer from "App/render/webgl/layers/ssao_layer";
import RenderObject from "App/geometry/objects/object";
import View from "App/view/view";
import ImageDataLoader from "App/utils/textures/image_data_loader";
import ModelStatistics from "App/model_statistics";
import BinsFactory from "App/render/webgl/nodes/bins/factory";
import Line from "App/geometry/objects/line";
import Texture from "App/render/webgl/renderables/texture";
import ShadersFactory from "App/render/webgl/shaders/factory";
import PassesFactory from "App/render/webgl/passes/factory";
import Renderable from "App/render/webgl/renderables/renderable";
import ShapeBatchNode from "App/render/webgl/nodes/shape_batch_node";

import {
    AlphaTransparencyBinTypeName,
    BillboardBinTypeName,
    ChunkLODBinTypeName,
    LinesBinTypeName,
    NewHUDBinTypeName,
    OpaqueBinTypeName,
    OpaqueNoTextureBinTypeName,
    OutlinesBinTypeName,
    ParticlesBinTypeName,
    PoiBinTypeName,
    PointCloudsBinTypeName,
    PointsBinTypeName,
    ShadowVolumesBinTypeName,
    SkyBoxBinTypeName,
    SkyDomeBinTypeName,
    TextBinTypeName,
    TransparentBinTypeName,
    TransparentInternalBinTypeName,
    TransparentNoTextureBinTypeName,
    TransparentNoTextureInternalBinTypeName
} from "App/render/webgl/nodes/bins/type_names";
import {
    DepthLowResPassTypeName, DepthPassTypeName, ObjectIdPassTypeName, PrimitiveIdPassTypeName,
    ScreenPassTypeName,
    ScreenPassAllOpaqueTypeName,
    ScreenPassAllTransparentTypeName,
    ScreenWithShadowVolumesPassTypeName
} from "App/render/webgl/passes/type_names";

export enum ERenderFiler {
    keepCameraAbove =    1,
    pickable =           2,
    alwaysOnTop =        4,
    isSide =             8,
    isCap =             16
}

/**
 * Render with WebGL 1.0
 *
 * @class
 * @extends AbstractRenderGraph
 */
export default
class WebGLRenderGraph extends AbstractRenderGraph {
    private _bins: Container;
    private _shaders: Container;
    private _framebuffers: Container;
    private _passes: Container;
    private _layers: Container;

    private _activeFramebuffer: Framebuffer = null;
    private _temporaryGLResources: { tempTextureID: WebGLTexture };
    private _renderablesToRelease: any[];
    private _updateGLBuffersScheduled: boolean;
    private _reCreateGLBuffersScheduled: boolean;
    private _reserveGLBuffersSizeScheduled: boolean;

    private _pgc: {backgroundColor: number[]} = { backgroundColor: [0, 0, 0, 0]};

    constructor(aSceneAPI, aRenderer, aContext) {
        super(aSceneAPI, aRenderer, aContext);

        this._bins = new Container();
        this._shaders = new Container();
        this._framebuffers = new Container();
        this._passes = new Container();

        // create render bins
        this.createRenderBins();

        // create render passes (shaders + options)
        this.createShaders();

        // create passes (for rendering to buffers)
        this.createPasses();

        // temporary 1x1 texture which is used until the regular texture data will be available
        this._temporaryGLResources = {
            tempTextureID: null
        };

        this._renderablesToRelease = [];

        this._updateGLBuffersScheduled = false;
        this._reCreateGLBuffersScheduled = false;
        this._reserveGLBuffersSizeScheduled = false;

        this._layers = new Container();
        this._layers.add('ssao', new SSAOLayer(this));
        this._layers.add('shadowMaps', new ShadowMapLayer(this));
        this._layers.add('hud', new HUDLayer(this));
        this._layers.add('controls', new ControlsLayer(this));
    }

    /**
     * @override
     */
    public release() {
        // Release parent objects
        super.release();

        this._bins.removeAll();
        this._shaders.forEach('release');
        this._framebuffers.forEach('release');
        this._passes.forEach('release');
        this._layers.forEach('release');

        this._releaseTemporaryGLResourceData();
    }

    /**
     * @override
     * @return {WebGLContext}
     */
    public getContext(): WebGLContext | Canvas2DContext {
        return this._context;
    }

    public getRenderBins(): Container {
        return this._bins;
    }

    public getShaders(): Container {
        return this._shaders;
    }

    public getPasses(): Container {
        return this._passes;
    }

    public getFramebuffers(): any {
        return this._framebuffers.getObjects();
    }

    public getFramebuffersContainer(): Container {
        return this._framebuffers;
    }

    public getLayer(aName: string) {
        return this._layers.get(aName);
    }

    /**
     * @override
     */
    public createFramebuffers() {
        this._framebuffers.removeAll();

        let gl = (this._context as WebGLContext).getWebGLRenderingContext();
        let contentSize = this._context.getCanvas().getContentSize();
        let sceneAPI = this._renderer.getDocument().getSceneAPI();
        let renderSettingsModule = sceneAPI.renderSettingsModule;

        let scale = 1.0;
        this._framebuffers.add('screen',
            new Framebuffer(this, {
                scale: scale,
                size: [Math.floor(contentSize[0] * scale), Math.floor(contentSize[1] * scale)],
                colorTextureFiltering: gl.NEAREST,
                colorTexturePixelDataType: gl.UNSIGNED_BYTE,
                screen: true
            })
        );

        // TODO: create the color and depth accumulation buffers only when they are needed (we have some transparent object in the scene)
        scale = 1.0 / renderSettingsModule.getQualityPerformanceFactor();
        this._framebuffers.add('colorAccumulation',
            new Framebuffer(this, {
                scale: scale,
                size: [Math.floor(contentSize[0] * scale), Math.floor(contentSize[1] * scale)],
                colorTextureFiltering: gl.NEAREST,
                colorTexturePixelDataType: gl.UNSIGNED_BYTE,
                createColorTexture: true,
                createDepthTexture: false,
                createDepthRenderBuffer: false
            })
        );

        scale = 1.0 / renderSettingsModule.getQualityPerformanceFactor();
        this._framebuffers.add('depthAccumulation',
            new Framebuffer(this, {
                scale: scale,
                size: [Math.floor(contentSize[0] * scale), Math.floor(contentSize[1] * scale)],
                colorTextureFiltering: gl.NEAREST,
                colorTexturePixelDataType: gl.UNSIGNED_BYTE,
                createColorTexture: true,
                createDepthTexture: false,
                createDepthRenderBuffer: false
            })
        );

        scale = 1.0 / renderSettingsModule.getQualityPerformanceFactor();
        this._framebuffers.add('depth',
            new Framebuffer(this, {
                scale: scale,
                size: [Math.floor(contentSize[0] * scale), Math.floor(contentSize[1] * scale)],
                colorTextureFiltering: gl.NEAREST,
                colorTexturePixelDataType: gl.UNSIGNED_BYTE,
                createColorTexture: true,
                createDepthTexture: false,
                createDepthRenderBuffer: true
            })
        );

        scale = 16 / contentSize[1]; // 4 is the required resolution in y dimension
        this._framebuffers.add('lowResDepth',
            new Framebuffer(this, {
                scale: scale,
                size: [Math.floor(contentSize[0] * scale), Math.floor(contentSize[1] * scale)],
                colorTextureFiltering: gl.NEAREST,
                colorTexturePixelDataType: gl.UNSIGNED_BYTE,
                createColorTexture: true,
                createDepthTexture: false,
                createDepthRenderBuffer: true
            })
        );

        scale = 1.0 / renderSettingsModule.getQualityPerformanceFactor();
        this._framebuffers.add('objectID',
            new Framebuffer(this, {
                scale: scale,
                size: [Math.floor(contentSize[0] * scale), Math.floor(contentSize[1] * scale)],
                colorTextureFiltering: gl.NEAREST,
                colorTexturePixelDataType: gl.UNSIGNED_BYTE,
                createColorTexture: true,
                createDepthTexture: false,
                createDepthRenderBuffer: true
            })
        );

        this._framebuffers.add('primitiveID', this._framebuffers.get('depth')); // reuse depth buffer for primitive id rendering
        this._framebuffers.add('opaqueDepth', this._framebuffers.get('depth')); // reuse depth buffer for primitive id rendering
    }

    /**
     * @override
     */
    public createRenderBins() {
        this._bins.removeAll();
        this._bins.fill(BinsFactory.createBins(this._sceneAPI, this));
    }

    /**
     * @override
     */
    public createShaders() {
        this._shaders.fill(ShadersFactory.createShaders(this._sceneAPI, this));
    }

    /**
     * @override
     */
    public createPasses() {
        this._passes.fill(PassesFactory.createPasses(this._sceneAPI, this));
    }

    public addShape(aShape: RenderObject): boolean {
        let bin = this._decideTargetBin(aShape);
        if (!bin) {
            return false;
        }

        return bin.addShape(aShape);
    }

    public removeShape(aShape: RenderObject) {
        super.removeShape(aShape);

        // instead of searching for the shape in the render graph, we take the shape.renderNode reference and
        // notify parent nodes about the deletion
        let renderGraphObject = aShape.getRenderGraphObject();
        if (!renderGraphObject) {
            return false;
        }

        // clear reference to render node
        renderGraphObject.removeShape(aShape);

        aShape.detachFromRenderGraphObject();
    }

    public initialize() {
        super.initialize();

        this.initializePasses();
        this.initializeFramebuffers();
        this.initializeShaders();

        // init gl states
        this.initGlStates();

        this._createTemporaryGLResourceData();
    }

    public initializePasses() {
        // logInfo("RG: Initialize passes");
        this._passes.forEach("release");
        this._passes.forEach("initialize");
    }

    public initializeShaders() {
        // logInfo("RG: Initialize shaders");
        this._shaders.forEach('release');
        this._shaders.forEach('initialize');
    }

    public initializeFramebuffers() {
        this._framebuffers.forEach('release');
        this.createFramebuffers();
        this._framebuffers.forEach('initialize');

        // Invalidate framebuffer for layers
        this._layers.forEach('invalidateFramebuffers');
    }

    public getTemporaryGLResourceData(): { tempTextureID: WebGLTexture } {
        return this._temporaryGLResources;
    }

    public initGlStates() {
        let gl = (this._context as WebGLContext).getWebGLRenderingContext();

        gl.enable(gl.DEPTH_TEST);
        gl.disable(gl.BLEND);
        gl.depthFunc(gl.LEQUAL);
    }

    public resize(aView: View) {
        /* jshint unused:vars */

        // we need to recreate the buffers from scratch as the resolution changes
        this.initializeFramebuffers();

        // Tell this to layers
        this._layers.forEach('onResize', [aView]);
    }

    public notifyMeshCreated(aShape: RenderObject) {
        let renderGraphObject = aShape.getRenderGraphObject();
        // notify the renderNode (which is a ShapeBatchNode instance) about this event
        if (renderGraphObject) {
            (renderGraphObject as ShapeBatchNode).notifyMeshCreated(aShape);
        }
    }

    public notifySetTextureDataForShapes(aShapes: RenderObject, aImageData: ImageDataLoader) {
        for (let key in aShapes) {
            let renderGraphObject = aShapes[key].getRenderGraphObject();
            if (renderGraphObject) {
                let textureNode = renderGraphObject.findTextureNode();
                textureNode.setTextureData(aImageData);
            }
        }
    }

    public notifySetTextureData(aUrl: string, aImageData: ImageDataLoader) {
        this._bins.forEach('notifySetTextureData', [aUrl, aImageData]);
    }

    public scheduleReserveGLBuffersSize() {
        this._reserveGLBuffersSizeScheduled = true;
    }

    public scheduleReCreateGLBuffers() {
        this._reCreateGLBuffersScheduled = true;
    }

    public scheduleShapesNeedUpdate() {
        this._updateGLBuffersScheduled = true;

        // On shapes visibility change invalidate shadow map
        this._layers.get('shadowMaps').invalidateTexture();
    }

    public lightingSettingsChanged() {
        this.initializeShaders();
    }

    public scheduleReleaseGLObject(aRenderable: Renderable) {
        if (aRenderable) {
            this._renderablesToRelease.push(aRenderable);
        }
    }

    public viewpointChanged() {
        super.viewpointChanged();

        // invalidate the passes content (e.g. buffers with objectID or primitiveID) in case that the viewpoint has changed
        this._framebuffers.forEach('invalidateContent');
    }

    public tick() {
        this._bins.get(ParticlesBinTypeName).tick();
    }

    public setActiveFramebuffer(aFramebuffer: Framebuffer) {
        this._activeFramebuffer = aFramebuffer;
    }

    public getActiveFramebuffer(): Framebuffer {
        return this._activeFramebuffer;
    }

    public countShapesAndUniqueTextures(aStatistics: ModelStatistics) {
        this._bins.forEach('countShapesAndUniqueTextures', [aStatistics]);
    }

    public render(aView: View) {
        if (this._reserveGLBuffersSizeScheduled) {
            this._bins.forEach('reserveBuffers');
            this._reserveGLBuffersSizeScheduled = false;
        }

        if (this._reCreateGLBuffersScheduled) {
            this._bins.forEach('reCreateBuffers');
            this._reCreateGLBuffersScheduled = false;
        }

        if (this._updateGLBuffersScheduled) {
            this._bins.forEach('updateBuffers');

            // invalidate the framebuffers content as when e.g. visibility changes, then we need object ids in the object id buffer
            this._framebuffers.forEach('invalidateContent');
            this._updateGLBuffersScheduled = false;
        }

        for (let renderable of this._renderablesToRelease) {
            renderable.release();
        }
        this._renderablesToRelease = [];

        // Variables
        let shadowsModule = this._sceneAPI.shadowsModule;
        let lightingModule = this._sceneAPI.lightingModule;

        // Prepare screen for drawing
        // --------------------------
        let screenBuffer = this._framebuffers.get('screen');
        let scale = screenBuffer.parameters.scale;
        let gl = (this._context as WebGLContext).getWebGLRenderingContext();
        let position = aView.getPosition();
        let size = aView.getSize();

        screenBuffer.bind();
        gl.enable(gl.SCISSOR_TEST);
        gl.viewport(position[0] * scale, position[1] * scale, size[0] * scale, size[1] * scale);
        gl.scissor(position[0] * scale, position[1] * scale, size[0] * scale, size[1] * scale);

        this._pgc.backgroundColor = this._sceneAPI.backgroundModule.getBackgroundColor();
        gl.clearColor(
            this._pgc.backgroundColor[0] / 255.0,
            this._pgc.backgroundColor[1] / 255.0,
            this._pgc.backgroundColor[2] / 255.0,
            this._sceneAPI.backgroundModule.getTransparentBackground() ? 0.0 : 1.0
        );

        // as the preserveDrawingBuffer parameter of canvas is set to true, we need to clear the draw buffer here
        gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

        // Render some information about scene to framebuffers needed later
        this._layers.forEach('prerender', [aView]);

        // Render to SSAO if enabled
        this._layers.get('ssao').render(aView);
        if (lightingModule.getSSAO().getDebugBuffer() > 0) {
            return; // Test if SSAO is in debug mode
        }

        // Render to ShadowMap if enabled
        this._layers.get('shadowMaps').render(aView);
        if (shadowsModule.getShadowMapDebugBuffer() > 0) {
            return; // Test if ShadowMap is in debug mode
        }

        // Render all to screen
        if (shadowsModule.getShadowVolumesEnabled()) {
            this._passes.get(ScreenWithShadowVolumesPassTypeName).render(
                aView,
                screenBuffer
            );
        } else {
            if (this._sceneAPI.renderSettingsModule.getForceGlobalTransparencyOverride()) {
                let transparencyValue =  this._sceneAPI.renderSettingsModule.getForceGlobalTransparencyOverrideValue();
                if (transparencyValue === 0.0) {
                    this._passes.get(ScreenPassAllOpaqueTypeName).render(
                        aView,
                        screenBuffer
                    );
                }
                else {
                    this._passes.get(ScreenPassAllTransparentTypeName).render(
                        aView,
                        screenBuffer
                    );
                }
            }
            else if (!this._sceneAPI.renderSettingsModule.getElementTransparencyEnabled()) {
                this._passes.get(ScreenPassAllOpaqueTypeName).render(
                    aView,
                    screenBuffer
                );
            }
            else {
                this._passes.get(ScreenPassTypeName).render(
                    aView,
                    screenBuffer
                );
            }
        }

        // Here might be called gl.clear on depth buffer
        // ---------------------------------------------
        // Render HUD
        this._layers.get('hud').render(aView);

        // Render controls to the controlTexture
        this._layers.get('controls').render(aView);
    }

    public renderDepth(aView: View, aProperties: any) {
        let pass = this._passes.get(DepthPassTypeName);
        pass.render(aView);
        return pass.readPoints(aView, aProperties);
    }

    /***
     * This consider in rendering only the terrain marked objects (or all geometry when the keep camera above all is set)
     *
     * @param aView
     * @param aProperties
     * @returns {*}
     */
    public renderDepthBellowCamera(aView: View, aProperties: any) {
        let pass = this._passes.get(DepthLowResPassTypeName);
        if (pass.render(aView, {type: "cameraAboveTerrain"})) {
            return pass.readPoints(aView, aProperties);
        } else {
            return [];
        }
    }

    public renderDepthCustomCamera(aView: View, aProperties: any) {
        let pass = this._passes.get(DepthLowResPassTypeName);
        pass.render(aView, {type: "cameraCollision"});

        return pass.readPoints(aView, aProperties);
    }

    public renderGeometryObjectUID(aView: View, aProperties: any) {
        let pass = this._passes.get(ObjectIdPassTypeName);
        pass.render(aView);
        return pass.readGeometryObjectUIDValue(aView, aProperties);
    }

    public renderPrimitiveId(aView: View, aProperties: any) {
        // first we try to pick object id under mouse cursor
        let pickedGeometryObjectUIDInfo = this.renderGeometryObjectUID(aView, aProperties);
        if (pickedGeometryObjectUIDInfo.shapeUID === 0) {
            // no object is under mouse cursor so we stop primitive id picking
            return 0;
        }
        let geometryObjectUID = pickedGeometryObjectUIDInfo.shapeUID;

        // look for shape based on picked shape uid
        let models = this._renderer.getDocument().getModelsList(true);
        let geometryObject = null;
        for (let key in models) {
            geometryObject = models[key].getGeometryObjectByUID(geometryObjectUID);
            if (geometryObject) {
                break;
            }
        }

        if (!geometryObject) {
            return null;
        }

        let pass = this._passes.get(PrimitiveIdPassTypeName);
        pass.render(aView, geometryObject);
        let pickingInfo = pass.readPrimitiveIdValue(aView, aProperties);

        // enhance the picking info with the picked geometry object id
        pickingInfo.geometryObjectUID = geometryObjectUID;
        pickingInfo.shapeRef = geometryObject;

        return pickingInfo;
    }
    /**
     * @override
     * @param {Shape} aShape
     * @returns {null|RenderBinNode}
     */
    private _decideTargetBin(aShape: RenderObject) {
        switch (aShape.getType()) {
            // case "HUD": return this._bins.get(HUDBinBinTypeName); // Bin without shapes
            case "POI":
                return this._bins.get(PoiBinTypeName);
            case "SKYBOX":
                return this._bins.get(SkyBoxBinTypeName);
            case "SKYDOME":
                return this._bins.get(SkyDomeBinTypeName);
            case "PARTICLES":
                return this._bins.get(ParticlesBinTypeName);
            case "POINT":
                return this._bins.get(PointsBinTypeName);
            case "OUTLINE":
                return this._bins.get(OutlinesBinTypeName);
            case "TEXT":
                return this._bins.get(TextBinTypeName);
            case "POINTCLOUD":
                return this._bins.get(PointCloudsBinTypeName);
            case "BILLBOARD":
                return this._bins.get(BillboardBinTypeName);

            case "LINE":
                if ((aShape as Line).getRenderAsOutline()) {
                    return this._bins.get(OutlinesBinTypeName);
                }
                return this._bins.get(LinesBinTypeName);

            case "SVSIDESHAPE":
            case "SVCAPSHAPE":
                return this._bins.get(ShadowVolumesBinTypeName);

            case "CHUNKLODNODESHAPE":
                return this._bins.get(ChunkLODBinTypeName);

            case "HUDSHAPE":
                return this._bins.get(NewHUDBinTypeName);

            case "SHAPE":
                if (aShape.isOpaque()) {
                    if (aShape.hasAlphaTransparency()) {
                        return this._bins.get(AlphaTransparencyBinTypeName);
                    } else if (aShape.hasTexture()) {
                        return this._bins.get(OpaqueBinTypeName);
                    } else {
                        return this._bins.get(OpaqueNoTextureBinTypeName);
                    }
                } else {
                    if (!aShape.getModel().isInternal()) {
                        if (aShape.hasTexture()) {
                            return this._bins.get(TransparentBinTypeName);
                        } else {
                            return this._bins.get(TransparentNoTextureBinTypeName);
                        }
                    }
                    else {
                        if (aShape.hasTexture()) {
                            return this._bins.get(TransparentInternalBinTypeName);
                        } else {
                            return this._bins.get(TransparentNoTextureInternalBinTypeName);
                        }
                    }
                }
                break;
        }

        return null;
    }

    private _createTemporaryGLResourceData() {
        let gl = (this._context as WebGLContext).getWebGLRenderingContext();

        if (this._temporaryGLResources.tempTextureID) {
            gl.deleteTexture(this._temporaryGLResources.tempTextureID);
        }

        this._temporaryGLResources.tempTextureID = Texture.createTempTexture(this._context);
    }

    private _releaseTemporaryGLResourceData() {
        let gl = (this._context as WebGLContext).getWebGLRenderingContext();

        if (this._temporaryGLResources.tempTextureID) {
            gl.deleteTexture(this._temporaryGLResources.tempTextureID);
        }
    }
}
